import { Card, HistoryResponse } from '@yleisradio/areena-types';
import { useEffect } from 'react';
import useSWR from 'swr';
import { useProgressCacheInvalidator } from 'contexts/ProgressCacheInvalidatorContext';
import { useTunnusContext } from 'contexts/TunnusContext';
import { fetchPlaybackHistory } from 'services/areena-api/fetchers';
import { getCardProgressIds } from 'utils/card';

interface HookReturn {
  readonly historyEntries: HistoryResponse | null;
}

// userId is key-only: scopes history per user so cache never leaks across users
type FetcherParameters = [
  'progress',
  programIds: string[],
  userId: string | undefined,
];

type FetcherType = (args: FetcherParameters) => Promise<HistoryResponse | null>;

const fetcher: FetcherType = ([, cardIds]) => fetchPlaybackHistory(cardIds);

export function useProgress(cardList: Card[]): HookReturn {
  const { isLoggedIn: isAuthenticated, user } = useTunnusContext();
  const programIds = getCardProgressIds(cardList);
  const key: FetcherParameters = ['progress', programIds, user?.userId];

  const { data: historyEntries = null, mutate: invalidate } = useSWR(
    isAuthenticated && key,
    fetcher
  );

  const { addInvalidator, deleteInvalidator } = useProgressCacheInvalidator();
  useEffect(() => {
    addInvalidator(invalidate);
    return () => deleteInvalidator(invalidate);
  }, [invalidate, addInvalidator, deleteInvalidator]);

  return {
    historyEntries,
  };
}
